import { UseQueryResult } from '@tanstack/react-query';
import useAuth from '../auth/useAuth';
import { rootRealmId } from '../businessEntity/constants';
import { useBusinessEntityByIdQuery } from '../businessEntity/queries';
import { useCurrentUserQuery } from '../user/queries';
import { useUserMePermitsQuery } from './queries';

/**
 * The purpose of "global permits" is to be able to fetch permits that can be used in the entire app, without the
 * context of a specific entity. A main case for this is when deciding which navigation items that should be presented.
 * Permits has to be fetched from some different sources for different users:
 * 1. Users on root realm should get their permits from api/v2/user/me/permit. This is not affected by capabilty bundles.
 * 2. Users placed on regions should use same permits as root users, see 1 above.
 * 3. All other users (i.e. users placed on accounts) should get permits that are filtered by capabilty bundles. This is
 *    achieved by fetching the permits from the parent entity.
 */
export function useGlobalPermits() {
  const { isAuthenticated } = useAuth();
  const { data: currentUser } = useCurrentUserQuery({
    enabled: isAuthenticated,
  });

  const realmId = currentUser?.realm.id;
  const isRoot = realmId === rootRealmId;
  const realm = useBusinessEntityByIdQuery<RealmDto>(realmId, ['parents'], {
    enabled: Boolean(realmId && !isRoot),
  });

  const parentId = realm.data?.parentIds?.[0];
  const parent = useBusinessEntityByIdQuery<RealmDto>(parentId, ['permits'], {
    enabled: Boolean(parentId && !isRoot),
  });

  const isRegion = parent.data?.type === 'region';

  const currentUserPermits = useUserMePermitsQuery(isRoot || isRegion);

  const permits =
    (isRoot || isRegion ? currentUserPermits.data : parent.data?.permits) ?? [];

  const { isLoading, isSuccess, isError } = getStatus(
    realm,
    parent,
    currentUserPermits
  );

  const error = {
    message: isError ? 'Fetch permits failed' : null,
  };

  return { permits, isLoading, isError, isSuccess, error };
}

function getStatus(...queryResults: UseQueryResult<any, any>[]) {
  const isLoading = queryResults.some((queryResult) => queryResult.isLoading);

  if (isLoading) {
    return {
      isLoading: true,
      isSuccess: false,
      isError: false,
    };
  }

  const isError = queryResults.some((queryResults) => queryResults.isError);

  return isError
    ? {
        isLoading: false,
        isError: true,
        isSuccess: false,
      }
    : {
        isLoading: false,
        isError: false,
        isSuccess: true,
      };
}
